import { ReactElement, useState, useEffect, useRef } from 'react'
import { Header, Container } from '../subcomponents'
import { H1, H2 } from '../../../typography'
import { Timer, TostMessage, Modal } from '../../../components'
import {
  InfoList,
  InfoBadge,
  InfoWrapper,
  Section,
  TestSection
} from './subcomponents'
import { colors } from '../../../const/theme'
import ObjectiveTest from './ObjectiveTest'
import JumpToQuestions from './JunpToQuestions'
import TestFooter from './TestFooter'
import strings from '../../../locale/en'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { RootState } from '../../../redux/store'
import { Question } from '../../../redux/test/type'
import {
  saveAnswer,
  submitTestPaper,
  updateError,
  updateMarkForReview
} from '../../../redux/test/action'
import DescriptiveTest from './DescriptiveTest'

const Test = (): ReactElement => {
  const {
    sectionList = [],
    name,
    questionCount = 0,
    quesionList = [],
    studentId = '',
    duration = 0,
    testPaperId = '',
    error
  } = useSelector(
    (state: RootState) => ({
      sectionList: state.test?.testPaper?.sectionList,
      name: state.test?.testPaperDetails?.testPaper?.name,
      questionCount: state.test?.testPaperDetails?.testPaper?.questionCount,
      quesionList: state.test?.testPaper?.quesionList,
      studentId: state.user?.userInfo?.userDetail?.usersId,
      duration: state.test?.testPaperDetails?.testPaper?.duration,
      testPaperId: state.test?.testPaperDetails?.testPaper?.id,
      error: state.test?.error
    }),
    shallowEqual
  )
  const dispatch = useDispatch()
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [timeTaken, setTimeTaken] = useState(0)
  const [canSubmitTest, setCanSubmitTest] = useState(false)
  const question: Question = quesionList[currentQuestion]
  const timerRef = useRef()
  const isDescriptive = question?.questionType === 'Descriptive'
  const {
    modal: {
      test: { title, subTitle }
    }
  } = strings

  useEffect(() => {
    const timer: any = setInterval(() => setTimeTaken(timeTaken + 1), 1000)
    timerRef.current = timer

    return () => {
      clearInterval(timerRef.current)
    }
  }, [timeTaken])

  useEffect(() => {
    window.scrollTo(0, 0)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  return (
    <Container id="container">
      <Header>
        <H1>{name}</H1>
        <Timer />
      </Header>
      <InfoWrapper>
        <H2 color={colors.gray}>{`${strings.test.question} ${
          currentQuestion + 1
        }`}</H2>
        <InfoList>
          <InfoBadge pill bg="info">
            {`${question?.answeredMark} Marks`}
          </InfoBadge>
          <InfoBadge pill bg="light" text="danger">
            {`${question?.wrongMark} Marks`}
          </InfoBadge>
          <InfoBadge pill bg="info">
            {`Type: ${question?.questionType}`}
          </InfoBadge>
        </InfoList>
        <InfoList>
          {sectionList.map((section, index) => {
            return (
              <Section
                variant="outline-secondary"
                key={`section-${index}`}
                onClick={() => {
                  const selectedSection = sectionList.find(
                    (tempSection) =>
                      tempSection?.sectionName === section?.sectionName
                  )

                  if (selectedSection?.sectionQuesionList?.length) {
                    const index = quesionList.findIndex(
                      (qtn) =>
                        qtn?.id === selectedSection?.sectionQuesionList[0]?.id
                    )
                    setCurrentQuestion(index)
                  }
                }}
              >
                {section?.sectionName}
              </Section>
            )
          })}
        </InfoList>
      </InfoWrapper>
      <TostMessage
        show={!!error}
        onCloseHandler={() => dispatch(updateError(''))}
        message={error}
      />
      <TestSection>
        {isDescriptive ? (
          <DescriptiveTest question={question} />
        ) : (
          <ObjectiveTest question={question} />
        )}
        <JumpToQuestions
          quesionList={quesionList}
          question={question}
          onClickHandler={(id) => {
            const index = quesionList.findIndex((qtn) => qtn?.id === id)
            setCurrentQuestion(index)
          }}
        />
      </TestSection>
      <TestFooter
        onNext={() => {
          if (currentQuestion < questionCount) {
            clearInterval(timerRef.current)
            setTimeTaken(0)
            if (question?.answer) {
              dispatch(
                saveAnswer({
                  answer: question?.answer || '',
                  attempt: 1,
                  questionPaperId: question?.id,
                  studentId: studentId,
                  testDurationTime: duration,
                  testPaperId: testPaperId,
                  timeSpent: timeTaken
                })
              )
            }

            if (currentQuestion < questionCount - 1) {
              setCurrentQuestion(currentQuestion + 1)
            }
          }
        }}
        onPrevious={() => {
          if (currentQuestion > 0) {
            setCurrentQuestion(currentQuestion - 1)
          }
        }}
        onReview={() =>
          dispatch(
            updateMarkForReview({ id: question?.id, isMarkedForReview: true })
          )
        }
        onSubmit={() => setCanSubmitTest(true)}
      />
      {canSubmitTest && (
        <Modal
          handleCancel={() => setCanSubmitTest(false)}
          handleSubmit={() => {
            setCanSubmitTest(false)
            dispatch(submitTestPaper())
          }}
          title={title}
          description={subTitle}
        />
      )}
    </Container>
  )
}

export default Test
